import { createSlice } from '@reduxjs/toolkit';
import { calendarApiV3 as api } from 'api';

export const icalendarProviderSlice = createSlice({
    name: 'icalendarProvider',
    initialState: {
        pending: false,
        isConnected: false,
        providerType: '',
        credentialLevel: '',
        hasCredential: false,
        pendingHasCredential: true,
        pendingSync: true,
        hasSync: false,
        pendingHasSync: false,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        startHasCredential: (state, action) => {
            state.pendingHasCredential = true;
        },
        setIntegrationStatus: (state, action) => {
            state.isConnected = action.payload.isConnected;
            state.providerType = action.payload.providerType;
            state.credentialLevel = action.payload.credentialLevel;
            state.pending = false;
        },
        setHasCredential: (state, action) => {
            state.hasCredential = action.payload.hasCredential;
            state.pendingHasCredential = false;
        },
        setPendingSync: (state, action) => {
            state.pendingSync = action.payload;
        },
        startHasSync: (state, action) => {
            state.pendingHasSync = true;
        },
        setHasSync: (state, action) => {
            state.hasSync = action.payload;
            state.pendingHasSync = false;
        },
        resetState: (state, action) => {
            state.pending = false;
            state.isConnected = false;
            state.providerType = '';
            state.credentialLevel = '';
            state.hasCredential = false;
            state.pendingHasCredential = true;
            state.pendingSync = true;
            state.hasSync = false;
            state.pendingHasSync = false;
        },
    },
});

export const {
    start,
    startHasCredential,
    setIntegrationStatus,
    setHasCredential,
    setPendingSync,
    startHasSync,
    setHasSync,
    resetState,
} = icalendarProviderSlice.actions;

export const getPendingIntegration = (state) => state.icalendarProvider.pending;
export const getIsConnected = (state) => state.icalendarProvider.isConnected;
export const getProviderType = (state) => state.icalendarProvider.providerType;
export const getCredentialLevel = (state) => state.icalendarProvider.credentialLevel;
export const getHasCredential = (state) => state.icalendarProvider.hasCredential;
export const getPendingHasCredential = (state) => state.icalendarProvider.pendingHasCredential;
export const getPendingSync = (state) => state.icalendarProvider.pendingSync;
export const getPendingHasSync = (state) => state.icalendarProvider.pendingHasSync;
export const getHasSync = (state) => state.icalendarProvider.hasSync;

export const fetchCalendarIntegrationAction = (organizationId, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(start());
        api.get(`integrations?organizationId=${organizationId}`)
            .then((response) => {
                if (response.data && !response.data.deletedAt && response.data.status) {
                    dispatch(setIntegrationStatus({
                        isConnected: true,
                        providerType: response.data.provider,
                        credentialLevel: response.data.credentialLevel,
                    }));
                } else {
                    dispatch(setIntegrationStatus({
                        isConnected: false,
                        providerType: '',
                        credentialLevel: '',
                    }));
                }
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            })
            .catch((error) => {
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
};

export const enableCalendarIntegrationAction = (organizationId, data, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(start());
        api.post(`integrations?organizationId=${organizationId}`, {
            credentialLevel: data.credentialLevel,
            provider: data.provider,
            status: true,
        })
            .then((response) => {
                dispatch(setIntegrationStatus({
                    isConnected: true,
                    providerType: data.provider,
                    credentialLevel: data.credentialLevel,
                }));
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            })
            .catch((error) => {
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
};

export const disableCalendarIntegrationAction = (organizationId, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(start());
        api.post(`integrations/disable?organizationId=${organizationId}`)
            .then((response) => {
                dispatch(resetState());
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            })
            .catch((error) => {
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
};

export const fetchHasCredentialAction = (data, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startHasCredential());
        const { organizationId, calendarId } = data;
        let queryParams = `${organizationId ? `?organizationId=${organizationId}` : ''}`;
        const separator = queryParams ? '&' : '?';
        queryParams += `${calendarId ? `${separator}calendarId=${calendarId}` : ''}`;
        api.get(`integrations/has-credential${queryParams}`)
            .then((response) => {
                dispatch(setHasCredential(response.data));
                if (onSuccess) {
                    onSuccess(response.data);
                }
                return response;
            })
            .catch((error) => {
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
};

export const fetchHasSyncAction = (data, onSuccess, onFailure) => async (dispatch) => {
    try {
        dispatch(startHasSync());
        const { organizationId, calendarId } = data;
        let queryParams = `${organizationId ? `?organizationId=${organizationId}` : ''}`;
        const separator = queryParams ? '&' : '?';
        queryParams += `${calendarId ? `${separator}calendarId=${calendarId}` : ''}`;
        api.get(`integrations/has-sync${queryParams}`)
            .then((response) => {
                dispatch(setHasSync(response.data?.hasSync));
                if (onSuccess) {
                    onSuccess(response.data);
                }
                return response;
            })
            .catch((error) => {
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
}

export const syncCalendarAction = (data, onSuccess, onFailure) => async (dispatch) => {
    try {
        const { organizationId, calendarId, subscribe, start, end } = data;
        dispatch(setPendingSync(true));
        const baseUrl = calendarId ? `calendars/${calendarId}/sync` : `organizations/${organizationId}/sync`;
        api.post(baseUrl, {
            subscribe,
            start,
            end,
        })
            .then((response) => {
                if (onSuccess) {
                    onSuccess(response.data);
                }
                return response;
            })
            .catch((error) => {
                dispatch(setPendingSync(false));
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
}

export const checkSyncStatusAction = (data, onSuccess, onFailure) => async (dispatch) => {
    try {
        const { organizationId, calendarId } = data;
        const baseUrl = calendarId ? `calendars/${calendarId}/sync-state` : `organizations/${organizationId}/sync-state`;
        api.get(baseUrl)
            .then((response) => {
                if (
                    response.data?.state === 'not_found' ||
                    response.data?.state === 'completed' ||
                    response.data?.state === 'failed'
                ) {
                    dispatch(setPendingSync(false));
                } else {
                    dispatch(setPendingSync(true));
                }
                if (onSuccess) {
                    onSuccess(response.data);
                }
                return response;
            })
            .catch((error) => {
                dispatch(setPendingSync(false));
                if (onFailure) {
                    onFailure(error);
                }
            });
    } catch (e) {
        return console.error(e.message);
    }
}

export default icalendarProviderSlice.reducer;